import React, { useState } from 'react';
import { App, Button, Popconfirm, Tooltip } from 'antd';
import { vendorApi } from '@/utils/vendorApi';
import { Authorization, VendorItem } from '@/types';
import { holdsForVendor } from '@/store/vendorAccess';

const CONFIRMATION =
  "Regenerating will reset this vendor's keys. The vendor will receive a new invitation email and must complete the onboarding form again before transfers resume. Continue?";

interface ReinviteVendorButtonProps {
  vendor: VendorItem;
  authorization: Authorization | null;
  onReinvited: () => void | Promise<void>;
}

const ReinviteVendorButton: React.FC<ReinviteVendorButtonProps> = ({
  vendor,
  authorization,
  onReinvited,
}) => {
  const { message } = App.useApp();
  const [submitting, setSubmitting] = useState(false);
  const allowed = holdsForVendor(authorization, vendor.bu, 'vendors.create');
  if (!allowed) {
    return null;
  }
  const hasBu = Boolean(vendor.bu?.trim());

  const handleReinvite = async () => {
    if (!hasBu) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await vendorApi.reinviteVendor(vendor);
      if (result.status === 'regenerated' || result.status === 'created') {
        message.success(result.message || `Vendor ${result.vendor_id} re-invited`);
        await onReinvited();
        return;
      }
      if (result.status === 'skipped') {
        message.warning(result.message || 'Vendor was skipped (already exists without regenerate).');
        return;
      }
      message.error(result.message || 'Re-invite failed');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to re-invite vendor';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Tooltip title={hasBu ? undefined : 'Assign a business unit before re-inviting'}>
      <span>
        <Popconfirm
          title="Re-invite / Resend Onboarding"
          description={CONFIRMATION}
          okText="Re-invite"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
          onConfirm={handleReinvite}
          disabled={!hasBu}
        >
          <Button danger disabled={!hasBu} loading={submitting}>
            Re-invite / Resend Onboarding
          </Button>
        </Popconfirm>
      </span>
    </Tooltip>
  );
};

export default ReinviteVendorButton;
